import React, { useState, useEffect } from "react";
import {
  Typography,
  Container,
  Box,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import api from "../../../globals/axios";
import CustomAppBar from "../../../components/AppBar";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../../components/LoaderContainer";
import CustomProgramCard from "../../../components/Custom/CustomProgramCard";
import { useNavigate } from "react-router-dom";
import { isNotNullOrEmptyArray } from "../../../globals/utils";
import { CustomTitle } from "../../../components/Custom/CustomTypography";
import GenericButton from "../../../components/Custom/GenericButton";
import { useAlert } from "../../../components/Alert";

const Program = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCrop, setSelectedCrop] = useState("");
  const [cropList, setCropList] = useState([]);
  const { showAlert } = useAlert();

  useEffect(() => {
    loadPrograms();
    fetchFarmerCrops();
  }, []);

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const result = await api.get("/farm-activities/signedup-programs/");
      setData(result.data.data);
    } catch (err) {
      console.error("Error fetching programs:", err);
      showAlert("error", t("Failed to fetch programs"));
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFarmerCrops = async () => {
    try {
      const res = await api.get("/farmer/crops");
      setCropList(
        res.data.map((crop) => ({
          id: crop.commodity.id,
          name: crop.commodity.name,
          farmName: crop.farmland?.farm_name || "",
        }))
      );
    } catch (err) {
      console.error("Error fetching crops:", err);
      setCropList([]);
    }
  };

  const handleViewProgramDetails = (programId) => {
    navigate(`${programId}`);
  };

  const handleCropChange = async (event) => {
    const cropId = event.target.value;
    setSelectedCrop(cropId);
  };

  const fetchProgramsByCrop = (cropId) => {
    if (!cropId) return;
    navigate(`/farmer/programs/signup/${cropId}`);
  };

  const renderProgramData = (program) => {
    return [
      {
        label: t("Program Name"),
        value: program.program_name || t("N/A"),
        type: "text",
      },
      {
        label: t("Crop Name"),
        value: program.commodity_name || t("N/A"),
        type: "text",
      },
      {
        label: t("Start Date"),
        value: program.start_date || t("N/A"),
        type: "text",
      },
    ];
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            maxHeight: "calc(100vh - 8rem)",
          }}
        >
          <CustomTitle>{t("My Programs")}</CustomTitle>
          {/* Show subscribed programs if any */}
          {isNotNullOrEmptyArray(data) ? (
            <Box sx={{ overflowY: "auto" }}>
              {data.map((program) => (
                <Box key={program.signup_id}>
                  <CustomProgramCard
                    id={program.signup_id}
                    data={renderProgramData(program)}
                    onView={() => handleViewProgramDetails(program.signup_id)}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
              {t("No programs Signed up.")}
            </Typography>
          )}
          <Card sx={{ p: 1, textAlign: "center" }}>
            <CardContent>
              {isNotNullOrEmptyArray(cropList) ? (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <FormControl fullWidth>
                    <InputLabel id="select-crop-label">
                      {t("Select Crop")}
                    </InputLabel>
                    <Select
                      labelId="select-crop-label"
                      id="select-crop"
                      value={selectedCrop}
                      onChange={handleCropChange}
                      label={t("Select Crop")}
                    >
                      {cropList.map((crop) => (
                        <MenuItem key={crop.id} value={crop.id}>
                          {crop.name} | {crop.farmName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <GenericButton
                    onClick={() => fetchProgramsByCrop(selectedCrop)}
                    backgroundColor="primary"
                    fontColor="#fff"
                    padding="10px"
                  >
                    {t("Fetch Programs")}
                  </GenericButton>
                </Box>
              ) : (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t("Create a crop to see available programs.")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Container>
      </LoaderContainer>
    </>
  );
};

export default Program;
